import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    PlayCircle, Package, Cube, Warning, CheckCircle, DownloadSimple, ClipboardText,
} from "@phosphor-icons/react";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";


/* ============================================================================
   StartManufacturingDialog — tech "claims" a case to begin manufacturing.
   ----------------------------------------------------------------------------
   Replaces the previous one-click Start button. Forces the tech to:
     1) Read the planner/QA spec (sheets to use, stage count, retainer count)
     2) Download the STL files
     3) Acknowledge they understand the spec
     4) Click "Start manufacturing" → case status → in_production,
        execution row is created on the case, audit log written.
   The actual logging of STL prints / aligners thermoformed lives in the
   ExecutionLogDialog (separate component) so the tech can log incremental
   progress through the day.
   ========================================================================== */

export function StartManufacturingDialog({ caseId, open, onClose, onStarted }) {
    const [data, setData] = useState(null);
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && caseId) {
            setAck(false);
            api.get(`/cases/${caseId}/manufacturing`).then((r) => setData(r.data));
        }
    }, [open, caseId]);

    const start = async () => {
        if (!ack) { toast.error("Please confirm you've read the spec"); return; }
        setBusy(true);
        try {
            await api.post(`/cases/${caseId}/manufacturing/start`);
            toast.success("Manufacturing started — case is yours");
            onStarted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to start");
        }
        setBusy(false);
    };

    if (!open) return null;
    const spec = data?.spec || {};
    const stlFiles = data?.stl_files || [];
    const hasSpec = !!spec.aligner_sheet_material_id;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="start-mfg-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PlayCircle size={20} weight="fill" className="text-primary" />
                        Start manufacturing
                    </DialogTitle>
                    <DialogDescription>Read the planner's spec, grab the STL files, then claim the case.</DialogDescription>
                </DialogHeader>

                {!data ? (
                    <div className="py-6 text-center text-muted-foreground text-sm">Loading…</div>
                ) : !hasSpec ? (
                    <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 flex items-start gap-3" data-testid="start-mfg-no-spec">
                        <Warning size={20} weight="fill" className="text-rose-700 shrink-0" />
                        <div className="text-sm">
                            <div className="font-semibold text-rose-900">Planner hasn't set the manufacturing spec yet.</div>
                            <div className="text-rose-800/80 mt-1">
                                Production can't start until the planner specifies which aligner sheet to use. Ping the planner on this case.
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Spec section */}
                        <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-4" data-testid="start-mfg-spec">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary mb-3">From planner / QA</div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <KV label="Aligner sheet" highlight>
                                    {spec.aligner_sheet_material ? (
                                        <>
                                            <span className="font-mono text-[10px] text-muted-foreground mr-1.5">{spec.aligner_sheet_material.sku}</span>
                                            <span className="font-bold">{spec.aligner_sheet_material.name}</span>
                                        </>
                                    ) : "—"}
                                </KV>
                                <KV label="Retainer sheet">
                                    {spec.retainer_sheet_material ? (
                                        <>
                                            <span className="font-mono text-[10px] text-muted-foreground mr-1.5">{spec.retainer_sheet_material.sku}</span>
                                            <span className="font-medium">{spec.retainer_sheet_material.name}</span>
                                        </>
                                    ) : <span className="text-muted-foreground italic">none</span>}
                                </KV>
                                <KV label="Stages">{spec.aligner_stages_count} ({spec.arches})</KV>
                                <KV label="Sheets to use">
                                    <span className="font-bold">{(spec.aligner_stages_count || 0) * (spec.sheets_per_stage || 1)}</span> aligner
                                    {spec.retainer_count > 0 && <> + <span className="font-bold">{spec.retainer_count}</span> retainer</>}
                                </KV>
                            </div>
                            {spec.notes && (
                                <div className="mt-3 text-xs italic bg-card border border-border rounded-md p-2.5" data-testid="start-mfg-spec-notes">"{spec.notes}"</div>
                            )}
                        </div>

                        {/* STL files */}
                        <div className="rounded-xl border border-border bg-card p-4" data-testid="start-mfg-stl">
                            <div className="flex items-center gap-2 mb-2">
                                <Cube size={14} weight="fill" className="text-primary" />
                                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">3D model files</div>
                            </div>
                            {stlFiles.length === 0 ? (
                                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                                    No STL/OBJ files on this case yet. Confirm with the planner before starting — you'll need the 3D models to print.
                                </div>
                            ) : (
                                <ul className="space-y-1">
                                    {stlFiles.map((f) => (
                                        <li key={f.id || f.filename} className="flex items-center justify-between gap-2 text-sm" data-testid={`start-mfg-stl-${f.id || f.filename}`}>
                                            <span className="font-mono text-xs truncate">{f.filename}</span>
                                            <a
                                                href={f.url || `/api/cases/files/${f.id}`}
                                                target="_blank" rel="noreferrer"
                                                className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1"
                                            >
                                                <DownloadSimple size={11} /> Download
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Acknowledgement */}
                        <label className="flex items-start gap-2 mt-3 cursor-pointer p-3 rounded-xl border border-border bg-card hover:bg-secondary/30 transition" data-testid="start-mfg-ack-label">
                            <input
                                type="checkbox"
                                checked={ack}
                                onChange={(e) => setAck(e.target.checked)}
                                className="mt-0.5 rounded border-border"
                                data-testid="start-mfg-ack"
                            />
                            <span className="text-sm">
                                I have downloaded the STL files and I will use <span className="font-bold">{spec.aligner_sheet_material?.sku}</span> for aligners
                                {spec.retainer_sheet_material && <> and <span className="font-bold">{spec.retainer_sheet_material.sku}</span> for retainers</>}
                                . I understand any deviation requires a written reason.
                            </span>
                        </label>
                    </>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={start} disabled={busy || !ack || !hasSpec} data-testid="start-mfg-submit">
                        {busy ? "Starting…" : "Start manufacturing"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ============================================================================
   ExecutionLogDialog — production tech logs progress while in_production.
     - STL prints completed
     - Aligners thermoformed (running count)
     - Retainers thermoformed (running count)
     - Actual sheets used (defaults to spec; deviation requires reason)
     - Free-text notes
   "Save" patches the execution row. "Complete & ready to ship" finalises +
   auto-decrements inventory stock.
   ========================================================================== */
export function ExecutionLogDialog({ caseId, open, onClose, onUpdated, onCompleted }) {
    const [data, setData] = useState(null);
    const [materials, setMaterials] = useState([]);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && caseId) {
            api.get(`/cases/${caseId}/manufacturing`).then((r) => {
                setData(r.data);
                const e = r.data?.execution || {};
                const s = r.data?.spec || {};
                setForm({
                    actual_aligner_sheet_material_id: e.actual_aligner_sheet_material_id || s.aligner_sheet_material_id || "",
                    actual_retainer_sheet_material_id: e.actual_retainer_sheet_material_id || s.retainer_sheet_material_id || "",
                    aligners_thermoformed_count: e.aligners_thermoformed_count || 0,
                    retainers_thermoformed_count: e.retainers_thermoformed_count || 0,
                    stl_models_printed_count: e.stl_models_printed_count || 0,
                    deviation_reason: e.deviation_reason || "",
                    notes: e.notes || "",
                });
            });
            api.get("/admin/inventory/materials").then((r) => setMaterials((r.data || []).filter((m) => m.is_active !== false)));
        }
    }, [open, caseId]);

    const aligner_options = useMemo(() => materials.filter((m) => m.category === "tpu_sheet"), [materials]);
    const retainer_options = useMemo(() => materials.filter((m) => ["retainer_sheet", "tpu_sheet"].includes(m.category)), [materials]);

    const spec = data?.spec || {};
    const exec_ = data?.execution || {};
    const deviates =
        (form.actual_aligner_sheet_material_id && form.actual_aligner_sheet_material_id !== spec.aligner_sheet_material_id) ||
        (form.actual_retainer_sheet_material_id && form.actual_retainer_sheet_material_id !== spec.retainer_sheet_material_id);

    const save = async () => {
        if (deviates && !form.deviation_reason.trim()) {
            toast.error("You're using a different sheet than the spec — please explain why");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                aligners_thermoformed_count: Number(form.aligners_thermoformed_count) || 0,
                retainers_thermoformed_count: Number(form.retainers_thermoformed_count) || 0,
                stl_models_printed_count: Number(form.stl_models_printed_count) || 0,
            };
            const r = await api.put(`/cases/${caseId}/manufacturing-execution`, payload);
            setData(r.data);
            toast.success("Execution log saved");
            onUpdated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const complete = async () => {
        if ((Number(form.aligners_thermoformed_count) || 0) <= 0) {
            toast.error("Log at least one thermoformed aligner before completing");
            return;
        }
        if (!await confirmDialog({ title: `Mark manufacturing complete? This will decrement ${form.aligners_thermoformed_count} aligner sheets + ${form.retainers_thermoformed_count || 0} retainer sheets from inventory.`, confirmText: "Confirm", tone: "warn" })) return;
        setBusy(true);
        try {
            // Save the latest before completing
            await save();
            await api.post(`/cases/${caseId}/manufacturing/complete`);
            toast.success("Manufacturing complete — ready to ship");
            onCompleted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    if (!open) return null;
    if (!data) return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent><div className="py-6 text-muted-foreground text-sm">Loading…</div></DialogContent>
        </Dialog>
    );

    const isComplete = !!exec_.completed_at;
    const totalSheets = (spec.aligner_stages_count || 0) * (spec.sheets_per_stage || 1);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="exec-log-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardText size={20} weight="fill" className="text-primary" />
                        Manufacturing log
                        {isComplete && (
                            <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Completed</span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Spec asks for {totalSheets} aligner sheet{totalSheets === 1 ? "" : "s"} of <span className="font-mono">{spec.aligner_sheet_material?.sku || "—"}</span>
                        {spec.retainer_count > 0 && <> + {spec.retainer_count} retainer sheet{spec.retainer_count === 1 ? "" : "s"}</>}.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-3 gap-3" data-testid="exec-log-counts">
                    <NumField label="STL prints done" value={form.stl_models_printed_count} onChange={(v) => setForm({ ...form, stl_models_printed_count: v })} disabled={isComplete} testid="exec-stl-count" />
                    <NumField label="Aligners thermoformed" value={form.aligners_thermoformed_count} onChange={(v) => setForm({ ...form, aligners_thermoformed_count: v })} target={totalSheets} disabled={isComplete} testid="exec-aligners-count" />
                    <NumField label="Retainers thermoformed" value={form.retainers_thermoformed_count} onChange={(v) => setForm({ ...form, retainers_thermoformed_count: v })} target={spec.retainer_count} disabled={isComplete} testid="exec-retainers-count" />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-1">
                    <div>
                        <Label>Aligner sheet actually used</Label>
                        <select
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={form.actual_aligner_sheet_material_id || ""}
                            onChange={(e) => setForm({ ...form, actual_aligner_sheet_material_id: e.target.value })}
                            disabled={isComplete}
                            data-testid="exec-actual-aligner"
                        >
                            {aligner_options.map((m) => <option key={m.id} value={m.id}>{m.sku} — {m.name} (stock: {m.current_stock})</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Retainer sheet actually used</Label>
                        <select
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                            value={form.actual_retainer_sheet_material_id || ""}
                            onChange={(e) => setForm({ ...form, actual_retainer_sheet_material_id: e.target.value })}
                            disabled={isComplete}
                            data-testid="exec-actual-retainer"
                        >
                            <option value="">— none —</option>
                            {retainer_options.map((m) => <option key={m.id} value={m.id}>{m.sku} — {m.name} (stock: {m.current_stock})</option>)}
                        </select>
                    </div>
                </div>

                {deviates && !isComplete && (
                    <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-3 mt-2" data-testid="exec-deviation-warning">
                        <div className="flex items-center gap-2 mb-2">
                            <Warning size={14} weight="fill" className="text-rose-700" />
                            <span className="text-xs font-bold text-rose-900 uppercase tracking-wide">Deviation from spec</span>
                        </div>
                        <Textarea
                            value={form.deviation_reason}
                            onChange={(e) => setForm({ ...form, deviation_reason: e.target.value })}
                            placeholder="Why are you using a different sheet than the planner asked for?"
                            rows={2}
                            data-testid="exec-deviation-reason"
                        />
                    </div>
                )}

                <div>
                    <Label>Notes (optional)</Label>
                    <Textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={2}
                        placeholder="e.g. Stage 7 required reprint due to bubble in resin."
                        disabled={isComplete}
                        data-testid="exec-notes"
                    />
                </div>

                <DialogFooter className="gap-2">
                    {!isComplete ? (
                        <>
                            <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
                            <Button variant="outline" onClick={save} disabled={busy} data-testid="exec-save">{busy ? "Saving…" : "Save progress"}</Button>
                            <Button onClick={complete} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="exec-complete">
                                <CheckCircle size={14} className="mr-1" /> Complete & ready to ship
                            </Button>
                        </>
                    ) : (
                        <Button onClick={onClose}>Close</Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function KV({ label, children, highlight }) {
    return (
        <div className={`rounded-md p-2.5 ${highlight ? "bg-white border border-primary/30" : "bg-secondary/30"}`}>
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">{label}</div>
            <div className="text-sm">{children}</div>
        </div>
    );
}

function NumField({ label, value, onChange, target, disabled, testid }) {
    const v = Number(value) || 0;
    const ok = target == null || v >= target;
    return (
        <div>
            <Label className="text-[10px]">{label}{target ? ` (target ${target})` : ""}</Label>
            <Input
                type="number"
                min="0"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className={target && v > 0 && !ok ? "border-amber-400" : ""}
                data-testid={testid}
            />
        </div>
    );
}
